// ═══════════════════════════════════════════════════════════════
// V·IANet Flow — OrthogonalEdge: Right-angle routing with rounded corners
// Passes markerEnd/markerStart props from React Flow UNCHANGED (already resolved
// as SVG URL strings from defaultEdgeOptions or edge-level marker objects).
// Only suppresses markers when data.arrowType === 'none'.
// ═══════════════════════════════════════════════════════════════

import { memo } from 'react';
import { getSmoothStepPath, EdgeLabelRenderer, BaseEdge, type EdgeProps } from '@xyflow/react';
import { FLOW_TOKENS as T } from '../../shared/flowTokens';

function OrthogonalEdgeComponent({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  data,
  selected,
  markerEnd: markerEndProp,
  markerStart: markerStartProp,
}: EdgeProps) {
  const d = (data as Record<string, unknown>) || {};
  const lineStyle = (d.lineStyle as string) || 'solid';
  const arrowType = d.arrowType as string | undefined;
  const edgeColor = (d.color as string) || T.color.connector;
  const strokeColor = selected ? T.color.nodeStrokeSelected : edgeColor;
  const dashArray = lineStyle === 'dashed' ? '8,4' : lineStyle === 'dotted' ? '2,3' : undefined;

  // ALWAYS pass markerEnd/markerStart props from React Flow through.
  // React Flow resolves defaultEdgeOptions.markerEnd into SVG marker URL strings
  // that reference <defs> created internally. If we override with a raw MarkerType
  // object here, React Flow won't have created the SVG <defs> for it and the arrow
  // will be invisible.
  //
  // For 'none', we suppress markers. For all other cases (including 'arrow',
  // 'bidirectional', 'open'), the markers are set on the edge object itself via
  // applyEdgeProperties/onConnect/defaultEdgeOptions, and React Flow resolves them
  // before passing them here as string props.
  let markerEnd: string | undefined = typeof markerEndProp === 'string' ? markerEndProp : undefined;
  let markerStart: string | undefined =
    typeof markerStartProp === 'string' ? markerStartProp : undefined;

  if (arrowType === 'none') {
    markerEnd = undefined;
    markerStart = undefined;
  }

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: T.connector.cornerRadius,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        markerStart={markerStart}
        style={{
          stroke: strokeColor,
          strokeWidth: T.connector.width,
          strokeDasharray: dashArray,
          strokeLinejoin: 'round',
          strokeLinecap: 'round',
        }}
      />

      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              background: T.connector.labelBg,
              border: `${T.connector.labelBorderWidth}px solid ${T.connector.labelBorder}`,
              borderRadius: T.connector.labelRadius,
              padding: `${T.connector.labelPadding.v}px ${T.connector.labelPadding.h}px`,
              fontSize: T.font.connectorLabel.size,
              fontWeight: T.font.connectorLabel.weight,
              fontFamily: T.font.family,
              color: T.color.connectorLabel,
              pointerEvents: 'all',
              lineHeight: 1,
            }}
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

export const OrthogonalEdge = memo(OrthogonalEdgeComponent);
